import { playSound } from '../../utils/utils';
import './BoardTileRelay.css';
import CoreboundIcon from './CoreboundIcon';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { usePopupTransition } from './popupTransition';
import {
  getBoardSquareLabel,
  BOARD_TILE_ABILITIES,
  getBoardSquareColor,
  isNegativeBoardSquare,
} from './doorBoard';
import { getPowerUpMultiplier, getPowerUpBonusPercent } from './powerUp';

export const RELAY_CHOICE_INSTRUCTIONS_ID = 'coreboundRelayChoiceInstructions';

export const getRelayTileSummary = (square) => {
  const effect = square.effect;
  const signed = (value) => (value < 0 ? `−${Math.abs(value)}` : `+${value}`);
  switch (effect?.type) {
    case 'trap':
      return {
        value: 'TRAP',
        caption: BOARD_TILE_ABILITIES.trap.summary,
      };
    case 'corruption':
      return { value: '−100', caption: 'Roll penalty' };
    case 'critical':
      return { value: '×2', caption: 'Critical hit' };
    case 'criticalMiss':
      return { value: '÷3', caption: 'Critical miss' };
    case 'shield':
      return { value: `${effect.amount}`, caption: 'Block damage' };
    case 'heal':
      return { value: `+${effect.amount} HP`, caption: 'Max HP' };
    case 'damage':
      return { value: `−${effect.amount} HP`, caption: 'Damage' };
    case 'shards':
      return { value: `+${effect.amount}`, caption: 'Shards' };
    case 'relic':
      return { value: 'CHEST', caption: 'Gain a relic' };
    case 'powerUp':
      return {
        value: `+${getPowerUpBonusPercent(getPowerUpMultiplier(effect))}%`,
        caption: 'Power Up',
      };
    case 'relay':
      return { value: 'RELAY', caption: 'Route again' };
    case 'gambit':
      return { value: 'REROLL', caption: 'Gambit' };
    case 'interference':
      return { value: '−1 DMG', caption: 'Weaken Core' };
    case 'sabotage':
      return { value: 'SABO', caption: 'Weaken its board' };
    case 'recharge':
      return { value: 'RESET', caption: 'Restore Anchor' };
    default:
      if (square.operation)
        return {
          value: `${square.operation === 'multiply' ? '×' : '÷'}${
            square.value
          }`,
          caption: 'Roll modifier',
        };
      if (!square.modifier) return { value: '0', caption: 'No modifier' };
      return {
        value: signed(square.modifier || 0),
        caption: square.modifier < 0 ? 'Roll penalty' : 'Roll bonus',
      };
  }
};

const RELAY_DIRECTIONS = [
  { offset: -5, name: 'North', arrow: '↑' },
  { offset: 1, name: 'East', arrow: '→' },
  { offset: 5, name: 'South', arrow: '↓' },
  { offset: -1, name: 'West', arrow: '←' },
];

export const BoardTileRelayPicker = ({ board, relay, onChoose }) => {
  const { close, popupClass } = usePopupTransition(undefined, {
    transitionKey: relay.id,
  });
  const root = useRef(null);
  const first = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const unlock = lockBodyScroll();
    first.current?.focus({ preventScroll: true });
    return () => {
      unlock();
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, [relay.id]);
  const choices = RELAY_DIRECTIONS.flatMap((direction) => {
    const index = relay.index + direction.offset;
    return relay.options.includes(index) && board[index]
      ? [{ ...direction, index, square: board[index] }]
      : [];
  });
  return createPortal(
    <div className="coreboundRelayPickerBackdrop">
      <section
        ref={root}
        className={`coreboundRelayPicker ${popupClass}`}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a neighboring Relay tile"
        aria-describedby={RELAY_CHOICE_INSTRUCTIONS_ID}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const buttons = root.current.querySelectorAll('button');
          const start = buttons[0];
          const end = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === start) {
            event.preventDefault();
            end?.focus();
          } else if (!event.shiftKey && document.activeElement === end) {
            event.preventDefault();
            start?.focus();
          }
        }}
      >
        <header>
          <CoreboundIcon name="relay" />
          <h2>Choose a Relay tile</h2>
        </header>
        <p id={RELAY_CHOICE_INSTRUCTIONS_ID}>
          Tap a neighbor to use its effect. Your die stays on square{' '}
          {relay.index + 1}.
        </p>
        <div
          className="coreboundRelayPickerChoices"
          role="group"
          aria-label="Available neighboring tiles"
        >
          {choices.map(({ index, square, name, arrow }, position) => {
            const summary = getRelayTileSummary(square);
            return (
              <button
                key={index}
                ref={position === 0 ? first : undefined}
                type="button"
                className="coreboundRelayPickerChoice"
                data-negative={isNegativeBoardSquare(square) || undefined}
                data-corrupted={
                  square.effect?.type === 'corruption' || undefined
                }
                style={{ '--relay-tile-accent': getBoardSquareColor(square) }}
                aria-label={`${name}, square ${
                  index + 1
                }: ${getBoardSquareLabel(square)}. ${
                  summary.caption
                }. Eligible Relay target. Select to activate this tile effect.`}
                onClick={() => {
                  playSound('/sounds/glitch-sound.mp3', {
                    preventOverlap: true,
                  });
                  close(() => onChoose?.({ id: relay.id, value: index }));
                }}
              >
                <span className="coreboundRelayPickerDirection">
                  <span aria-hidden="true">{arrow}</span> {name}
                </span>
                <strong>{summary.value}</strong>
                <span className="coreboundRelayPickerCaption">
                  {summary.caption}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>,
    document.body
  );
};

export const BoardTileRelayInstructions = ({ index }) => (
  <p
    id={RELAY_CHOICE_INSTRUCTIONS_ID}
    className="coreboundRelayChoicePrompt"
    role="status"
    aria-live="polite"
  >
    <strong>RELAY ACTIVATED · SQUARE {index + 1}</strong>
    Choose a highlighted neighboring tile to activate its effect. The die stays
    on its landing square.
  </p>
);

export const BoardTileRelayCell = ({
  index,
  label,
  relay,
  className,
  style,
  current,
  ariaLabel,
  title,
  onChoose,
  focusRef,
  children,
}) => {
  const isOrigin = relay?.index === index;
  const isTarget = !!relay?.options.includes(index);
  const Tile = relay ? 'button' : 'span';
  const relayState = isOrigin
    ? 'Relay origin.'
    : isTarget
    ? 'Eligible Relay target. Select to activate this tile effect.'
    : 'Not an eligible Relay target.';

  return (
    <Tile
      role={relay ? undefined : 'gridcell'}
      type={relay ? 'button' : undefined}
      disabled={relay ? !isTarget : undefined}
      ref={focusRef}
      tabIndex={isTarget ? 0 : undefined}
      className={`${className} ${isTarget ? 'is-relay-choice' : ''} ${
        relay && !isTarget && !isOrigin ? 'is-relay-unavailable' : ''
      } ${isOrigin ? 'is-relay-origin' : ''}`}
      style={style}
      aria-label={
        relay ? `Square ${index + 1}: ${label}. ${relayState}` : ariaLabel
      }
      aria-current={current ? 'true' : undefined}
      title={isTarget ? `Relay to ${label}` : title || label}
      {...(isTarget
        ? {
            onClick: () => {
              playSound('/sounds/glitch-sound.mp3', { preventOverlap: true });
              onChoose?.(index);
            },
          }
        : {})}
    >
      {isOrigin && (
        <small
          className="coreboundRelayTileMarker is-origin"
          aria-hidden="true"
        >
          RELAY
        </small>
      )}
      {isTarget && (
        <small className="coreboundRelayTileMarker" aria-hidden="true">
          <CoreboundIcon name="relay" />
        </small>
      )}
      {children}
    </Tile>
  );
};
